import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Users,
  Inbox,
  RefreshCw,
  ArrowRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { adminAPI } from '@/services/api';
import { StatusBadge, formatDateTime } from './adminUtils';

const StatCard = ({ label, value, hint, icon: Icon, tone }) => {
  const tones = {
    blue: { icon: 'bg-[#0888F8]/10 text-[#0888F8]', bar: 'bg-[#0888F8]' },
    sky: { icon: 'bg-[#78A0D0]/20 text-[#4A7AB0]', bar: 'bg-[#78A0D0]' },
    gold: { icon: 'bg-[#F8B010]/15 text-[#C48A08]', bar: 'bg-[#F8B010]' },
  };
  const t = tones[tone] || tones.blue;

  return (
    <div className="group relative rounded-2xl bg-white/80 backdrop-blur-sm p-5 ring-1 ring-[#E2EBF4] shadow-[0_8px_30px_-20px_rgba(8,136,248,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-24px_rgba(8,136,248,0.35)]">
      <div className={`absolute top-0 left-5 right-5 h-0.5 rounded-full ${t.bar} opacity-60`} />
      <div className="flex items-start justify-between gap-3 pt-1">
        <div>
          <p className="text-[13px] text-[#6B7C90] font-medium">{label}</p>
          <p
            className="text-[2rem] font-semibold text-[#1A2B3C] mt-1 tracking-tight leading-none"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            {value ?? '-'}
          </p>
          {hint && <p className="text-[11px] text-[#8A9BB0] mt-2">{hint}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.icon}`}>
          <Icon size={18} strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
};

export default function OverviewPanel() {
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await adminAPI.getOverview(token);
      setData(overview);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-[#0888F8]" size={28} />
      </div>
    );
  }

  const appt = data?.appointments || {};
  const regs = data?.registrations || {};
  const msgs = data?.messages || {};
  const activity = data?.recent_activity || [];

  return (
    <div className="space-y-8 animate-in fade-in duration-400">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[1.75rem] font-semibold text-[#1A2B3C] tracking-tight"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Overview
          </h1>
          <p className="text-sm text-[#6B7C90] mt-1">
            A quiet snapshot of what’s waiting for you.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 self-start text-[13px] font-medium text-[#5A6B7D] hover:text-[#0888F8] transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {!data?.database_available && (
        <div className="flex items-start gap-3 rounded-2xl bg-[#F8B010]/10 px-4 py-3.5 text-sm text-[#8A6208]">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <p>
            Database unavailable or email-only mode. Submissions still email staff; lists may stay empty until MongoDB is connected.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Today"
          value={appt.today}
          hint={`${appt.upcoming ?? 0} upcoming · ${appt.pending ?? 0} pending`}
          icon={CalendarDays}
          tone="blue"
        />
        <StatCard
          label="Appointments"
          value={appt.total}
          hint="All statuses"
          icon={CalendarDays}
          tone="sky"
        />
        <StatCard
          label="Registrations"
          value={regs.total}
          hint="Tour applications"
          icon={Users}
          tone="gold"
        />
        <StatCard
          label="Requests"
          value={msgs.pending}
          hint={`${msgs.unread ?? 0} unread · ${msgs.total ?? 0} total`}
          icon={Inbox}
          tone="blue"
        />
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 rounded-2xl bg-white/80 backdrop-blur-sm ring-1 ring-[#E2EBF4] overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between">
            <h2
              className="text-base font-semibold text-[#1A2B3C]"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Recent activity
            </h2>
          </div>
          {activity.length === 0 ? (
            <div className="px-5 py-14 text-center text-sm text-[#8A9BB0]">
              No recent activity yet.
            </div>
          ) : (
            <ul className="divide-y divide-[#F0F4F8]">
              {activity.map((item) => (
                <li
                  key={`${item.type}-${item.id}`}
                  className="px-5 py-3.5 flex items-center gap-3 hover:bg-[#0888F8]/[0.03] transition-colors"
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      item.type === 'appointment'
                        ? 'bg-[#0888F8]/10 text-[#0888F8]'
                        : item.type === 'registration'
                          ? 'bg-[#F8B010]/15 text-[#C48A08]'
                          : 'bg-[#78A0D0]/20 text-[#4A7AB0]'
                    }`}
                  >
                    {item.type === 'appointment' ? (
                      <CalendarDays size={15} strokeWidth={1.75} />
                    ) : item.type === 'registration' ? (
                      <Users size={15} strokeWidth={1.75} />
                    ) : (
                      <Inbox size={15} strokeWidth={1.75} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1A2B3C] truncate">{item.title}</p>
                    <p className="text-xs text-[#8A9BB0] truncate">
                      {item.type} · {item.subtitle}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 space-y-1">
                    <StatusBadge status={item.status} />
                    <p className="text-[11px] text-[#8A9BB0]">{formatDateTime(item.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2 rounded-2xl bg-white/80 backdrop-blur-sm ring-1 ring-[#E2EBF4] p-5">
          <h2
            className="text-base font-semibold text-[#1A2B3C] mb-4"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Jump to
          </h2>
          <div className="space-y-1.5">
            {[
              { to: '/admin/appointments', label: 'Appointments', accent: '#0888F8' },
              { to: '/admin/registrations', label: 'Registrations', accent: '#F8B010' },
              { to: '/admin/requests', label: 'Requests inbox', accent: '#78A0D0' },
            ].map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="flex items-center justify-between px-3 py-3 rounded-xl text-sm font-medium text-[#3D4F63] hover:bg-[#F7FAFD] transition-colors group"
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: action.accent }}
                  />
                  {action.label}
                </span>
                <ArrowRight
                  size={14}
                  className="text-[#8A9BB0] group-hover:text-[#0888F8] group-hover:translate-x-0.5 transition-all"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
